// =================================================
// IMPORTS
// -------------------------------------------------
import React from "react";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import {
  Grid,
  Card,
  CardContent,
  Typography,
  useTheme,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { DataGrid } from "@mui/x-data-grid";
import PollIcon from "@mui/icons-material/Poll";
import { Area, AreaChart, ResponsiveContainer, YAxis } from "recharts";
import { consumersSelectors } from "../redux/reducers/consumers";
import ThumbUpIcon from "@mui/icons-material/ThumbUp";
import ThumbDownIcon from "@mui/icons-material/ThumbDown";
import ThumbsUpDownIcon from "@mui/icons-material/ThumbsUpDown";
import MessageIcon from "@mui/icons-material/Message";
// =================================================
// COMPONENT
// -------------------------------------------------

const DashboardCBTIOverview = ({
  resultsByUser = {}, // Default to empty object to avoid property access issues
}) => {
  const { t } = useTranslation("components", {
    keyPrefix: "DashboardCBTIContent",
  });
  const theme = useTheme();
  const consumerEntities = useSelector((state) =>
    consumersSelectors.selectEntities(state),
  );
  const isXS = useSelector((state) => state.ui.isXS);
  const isSM = useSelector((state) => state.ui.isSM);
  // -----------------------------------------------
  // Browser URL location
  const navigate = useNavigate();

  // Define columns for the DataGrid
  const columns = [
    {
      field: "number",
      headerName: "User ID",
      width: 75,
    },
    {
      field: "name",
      headerName: "Email",
      width: 200,
    },
    {
      field: "sleepEfficiency",
      headerName: "Sleep Efficiency",
      width: 120,
      renderCell: (params) => {
        const data = Object.values(params?.value || {}).map((d) => ({
          x: d.x,
          y: d.md,
        }));
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <ResponsiveContainer width={100} height="100%">
              <AreaChart
                data={data}
                margin={{
                  top: 0,
                  right: 5,
                  left: 5,
                  bottom: 0,
                }}
              >
                <YAxis type="number" domain={[0, 1]} hide />
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={theme.palette.primary.main}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor={theme.palette.primary.main}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <Area
                  // type="monotone"
                  dot={true}
                  dataKey="y"
                  stroke={theme.palette.primary.main}
                  fill="url(#colorValue)"
                />
              </AreaChart>
            </ResponsiveContainer>
            <Typography variant="caption" className="ms-1">
              {(() => {
                const mostRecentEntry = Object.values(params?.value || {}).sort(
                  (a, b) => b.x[1] - a.x[1],
                )[0];
                return mostRecentEntry
                  ? `${Math.round(mostRecentEntry.md * 100)}%`
                  : "-";
              })()}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "isiScore",
      headerName: "ISI",
      width: 120,
      renderCell: (params) => {
        const data = Object.values(params?.value || {}).map((d) => ({
          x: d.x,
          y: d.y,
        }));
        return (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              height: "100%",
            }}
          >
            <ResponsiveContainer width={100} height="100%">
              <AreaChart
                data={data}
                margin={{
                  top: 0,
                  right: 5,
                  left: 5,
                  bottom: 0,
                }}
              >
                <YAxis type="number" domain={[0, 28]} hide />
                <defs>
                  <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
                    <stop
                      offset="5%"
                      stopColor={theme.palette.primary.main}
                      stopOpacity={0.8}
                    />
                    <stop
                      offset="95%"
                      stopColor={theme.palette.primary.main}
                      stopOpacity={0}
                    />
                  </linearGradient>
                </defs>
                <Area
                  dot={true}
                  dataKey="y"
                  stroke={theme.palette.primary.main}
                  fill="url(#colorValue)"
                />
              </AreaChart>
            </ResponsiveContainer>
            <Typography variant="caption" className="ms-1">
              {(() => {
                const mostRecentEntry = Object.values(params?.value || {})
                  .filter((val) => val.y)
                  .sort((a, b) => b.x.localeCompare(a.x))[0];
                return mostRecentEntry ? `${mostRecentEntry.y}` : "-";
              })()}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "continueScore",
      headerName: "Continue",
      width: 85,
      renderCell: (params) => {
        const value = parseInt(params?.value);
        const label =
          value === 4
            ? "very likely"
            : value === 3
              ? "likely"
              : value === 2
                ? "probable"
                : value === 1
                  ? "unlikely"
                  : value === 0
                    ? "very unlikely"
                    : "";
        return (
          <div>
            {value > 2 ? (
              <ThumbUpIcon
                style={{
                  color: theme.palette.success.main,
                  fontSize: 20,
                }}
              />
            ) : value === 2 ? (
              <ThumbsUpDownIcon
                style={{
                  color: theme.palette.warning.main,
                  fontSize: 20,
                }}
              />
            ) : value < 2 ? (
              <ThumbDownIcon
                style={{
                  color: theme.palette.error.main,
                  fontSize: 20,
                }}
              />
            ) : (
              "-"
            )}
            <Typography variant="caption" className="ms-1">
              {label}
            </Typography>
          </div>
        );
      },
    },
    {
      field: "cbtiProgress",
      headerName: "CBTI Progress",
      width: 140,
      renderCell: (params) => (
        <div>
          {(params?.value || []).map((completed, index) => (
            <PollIcon
              key={index}
              style={{
                color: completed
                  ? theme.palette.success.main
                  : theme.palette.text.disabled,
              }}
            />
          ))}
        </div>
      ),
    },
    {
      field: "sleepLogsProgress",
      headerName: "Sleep Logs",
      width: 200,
      renderCell: (params) => (
        <div>
          {(params?.value || []).map((completed, index) => (
            <svg key={index} width="2" height="20" style={{ marginRight: 1 }}>
              <rect
                width="2"
                height="20"
                fill={completed ? theme.palette.success.main : "#CCC"}
              />
            </svg>
          ))}
        </div>
      ),
    },
    {
      field: "mentionToTherapist",
      headerName: "Message",
      width: 75,
      renderCell: (params) =>
        params.value &&
        params.value !== "-" && (
          <Tooltip title={params?.value} arrow placement="left">
            <MessageIcon
              style={{
                color: theme.palette.warning.main,
                fontSize: 20,
              }}
            />
          </Tooltip>
        ),
    },
  ];

  // ===============================================
  // RENDER COMPONENT
  // -----------------------------------------------
  return (
    <Grid container>
      {!isSM && !isXS && (
        <Grid item xs={12}>
          <Typography variant="h2" className="my-2">
            {t("Sleep overview")}
          </Typography>
        </Grid>
      )}
      <Grid item xs={12} className="pe-xs-0 pe-sm-0 pe-md-0 pe-lg-3 pe-xl-3">
        <Card className="shadow-none ">
          <CardContent className="p-0">
            <DataGrid
              autosizeOnMount
              onCellClick={(params) => {
                navigate(`/dashboard-cbti?consumerId=${params.id}`, {
                  replace: false,
                });
              }}
              rows={Object.keys(resultsByUser).map((userId) => {
                const cbtiTasks = resultsByUser[userId]?.cbtiTickets || [];
                const cbtiProgress = cbtiTasks
                  .sort((a, b) =>
                    (a?.acronym || "").localeCompare(b?.acronym || ""),
                  )
                  .map((task) => (task?.isCompleted ? 1 : 0));

                const sleepLogTickets = (
                  resultsByUser[userId]?.sleepLogTickets || []
                )
                  .filter((task) => new Date(task?.dateAvailable) <= new Date())
                  .sort((a, b) =>
                    (a?.dateAvailable || "").localeCompare(
                      b?.dateAvailable || "",
                    ),
                  );
                const sleepLogsProgress = sleepLogTickets.map((task) =>
                  task?.isCompleted ? 1 : 0,
                );
                const isiScoreKeys = [
                  "CBCTIA_ISI_SUM",
                  "CBCTIB_ISI_SUM",
                  "CBCTIC_ISI_SUM",
                  "CBCTID_ISI_SUM",
                  "CBCTIE_ISI_SUM",
                ];
                const continueKeys = [
                  "CBCTIA25a",
                  "CBCTIB42a",
                  "CBCTIC43a",
                  "CBCTID41a",
                ];
                const mentionToTherapistKeys = [
                  "CBCTIA26",
                  "CBCTIB43",
                  "CBCTIC44",
                  "CBCTID45",
                  "CBCTIE45",
                ];
                const isiScores = resultsByUser[userId]?.cbtiTaskResponses.map(
                  (response) => {
                    const data = response.data || {};
                    const foundKey = isiScoreKeys.find((key) => key in data);
                    return foundKey
                      ? {
                          x: response.dateAvailable,
                          y: data[foundKey],
                        }
                      : undefined;
                  },
                );
                const continueScores = resultsByUser[userId]?.cbtiTaskResponses
                  .map((response) => {
                    const data = response.data || {};
                    const foundKey = continueKeys.find((key) => key in data);
                    return foundKey
                      ? {
                          x: response.dateAvailable,
                          y: data[foundKey],
                        }
                      : {
                          x: response.dateAvailable,
                          y: null,
                        };
                  })
                  .filter((score) => score.y)
                  .sort((a, b) => b.x.localeCompare(a.x));
                const mentionToTherapist = resultsByUser[
                  userId
                ]?.cbtiTaskResponses
                  .map((response) => {
                    const data = response.data || {};
                    const foundKey = mentionToTherapistKeys.find(
                      (key) => key in data,
                    );
                    return foundKey
                      ? {
                          x: response.dateAvailable,
                          y: data[foundKey],
                        }
                      : {
                          x: response.dateAvailable,
                          y: null,
                        };
                  })
                  .filter((score) => score.y)
                  .sort((a, b) => b.x.localeCompare(a.x));

                return {
                  id: userId,
                  number:
                    consumerEntities[userId]?.studyEnrollmentList?.[0]
                      ?.userStudyNumber ??
                    consumerEntities[userId]?.userNumber ??
                    "Unknown",
                  name: consumerEntities[userId]?.email || "-",
                  cbtiProgress,
                  sleepLogsProgress,
                  sleepEfficiency: resultsByUser[userId]?.sleepEfficiency || {},
                  isiScore: isiScores || [],
                  continueScore: continueScores[0]?.y || "-",
                  mentionToTherapist: mentionToTherapist[0]?.y || "-",
                };
              })}
              columns={columns}
              hideFooter
              autoHeight
            />
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

// =================================================
// EXPORT COMPONENT
export default DashboardCBTIOverview;
